import { ComponentType } from '@angular/cdk/portal';
import { InjectionToken, Provider } from '@angular/core';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';

import { BillingCounts, PricingPlan } from '@ninety/platform/models';
import { BillingV3Service } from '@ninety/platform/services/services/billing-v3.service';
import { ErrorService } from '@ninety/platform/services/services/error.service';
import {
  CHECKOUT_DIALOG,
  CONFIRM_DIALOG,
  CONFIRM_LICENSE_CHANGE_DIALOG,
  DialogOpener,
  DOWNGRADE_CONFIRMATION_DIALOG,
  DOWNGRADE_RETENTION_DIALOG,
  DOWNGRADE_RETENTION_STEP2_DIALOG,
  MANAGE_SEATS_DIALOG,
  PRICING_TIER_DIALOG,
} from '@ninety/platform/state';
import { selectCurrentUserIsAdminOrOwner } from '@ninety/platform/state/app-entities/users/users-state.selectors';
import { BillingStateActions } from '@ninety/platform/state/app-global/billing/billing-state.actions';
import {
  selectCompanyBillingCounts,
  selectFreemiumPlanId,
  selectHasDefaultPaymentMethod,
  selectIsFreemiumPlan,
  selectSortedPricingPlans,
  selectUseSubscriptionScheduleFlow,
} from '@ninety/platform/state/app-global/billing/billing-state.selectors';
import { SubscriptionActions } from '@ninety/platform/state/app-global/company/subscription/subscription-state.actions';
import { selectIsTrialSubscription } from '@ninety/platform/state/app-global/company/subscription/subscription-state.selectors';
import { NavMenuIntercomActions } from '@ninety/platform/state/app-global/intercom/intercom-state.actions';
import {
  AUTO_INCREMENT_SUBSCRIPTION_QUANTITY,
  BILLING_ERROR_NOTIFIER,
  CANCEL_LICENSE_CHANGE,
  CHANGE_LICENSE_COUNT,
  CheckoutComponent,
  MANAGE_SEATS_BILLING_COUNTS,
  CHECKOUT_BILLING_SERVICE,
  ConfirmDialogComponent,
  ConfirmLicenseChangeComponent,
  DOWNGRADE_CONFIRMED,
  DowngradeConfirmationDialogComponent,
  DowngradeRetentionDialogComponent,
  DowngradeRetentionStep2DialogComponent,
  FREEMIUM_PLAN_ID,
  GET_COMMERCIAL_MODEL_SUBSCRIPTIONS,
  GET_COMPANY_BILLING_COUNTS,
  GET_SETUP_URL,
  HAS_DEFAULT_PAYMENT_METHOD,
  IS_ADMIN_OR_OWNER,
  IS_TRIAL_SUBSCRIPTION,
  MANAGE_SEATS_IS_FREEMIUM_PLAN,
  ManageSeatsComponent,
  OPEN_CHECKOUT_MODAL,
  OPEN_PRICING_PLANS_MODAL,
  OPEN_RETENTION_STEP2_DIALOG,
  PricingTierDialogComponent,
  RECORD_CANCELLATION_REASON,
  SELECT_PLAN,
  SHOW_INTERCOM_MESSENGER,
  SORTED_PRICING_PLANS,
  UPDATE_NEXT_PHASE,
  USE_SUBSCRIPTION_SCHEDULE_FLOW,
  type BillingErrorNotifier,
} from '@ninety/ui/core';

/**
 * Binds the billing + subscription dialog-opener tokens (DEV-26105 T6b, Option B) to factories that
 * open the concrete dialog components via `MatDialog`. The dialog-opening effects (`billing-state.effects`,
 * `subscription-state.effects`) live in `platform/state` and inject only the tokens; this composition-root
 * wiring is the single place that depends on both the tokens and the `shared/ui/core` components, so the
 * `platform/state -> shared/ui/core` edge stays severed. Mirrors `auth-dialog.providers.ts`.
 *
 * DEV-26530 Part 8d: the billing dialogs moved into `@ninety/ui/core` and no longer import
 * `@ninety/platform/state` or `@ninety/platform/services` (those graphs — `BillingV3Service`,
 * `ErrorService` -> Datadog, NgRx selectors/actions — cannot be compiled by `ng-packagr` into the public
 * package). The bindings below supply the store reads/dispatches and platform services the dialogs
 * previously reached for directly, keeping the `shared/ui/core -> platform/state` edge severed. The
 * spinner control (`PRIMARY_SPINNER_CONTROL`) is already bound by `UPLOAD_DIALOG_PROVIDERS` (Part 8b).
 */
function provideDialog<In, Out>(
  token: InjectionToken<DialogOpener<In, Out>>,
  component: ComponentType<unknown>
): Provider {
  return {
    provide: token,
    useFactory:
      (matDialog: MatDialog): DialogOpener<In, Out> =>
      (config?: MatDialogConfig<In>) =>
        matDialog.open<unknown, In, Out>(component, config),
    deps: [MatDialog],
  };
}

export const BILLING_DIALOG_PROVIDERS: Provider[] = [
  provideDialog(CHECKOUT_DIALOG, CheckoutComponent),
  provideDialog(PRICING_TIER_DIALOG, PricingTierDialogComponent),
  provideDialog(CONFIRM_LICENSE_CHANGE_DIALOG, ConfirmLicenseChangeComponent),
  provideDialog(DOWNGRADE_CONFIRMATION_DIALOG, DowngradeConfirmationDialogComponent),
  provideDialog(DOWNGRADE_RETENTION_DIALOG, DowngradeRetentionDialogComponent),
  provideDialog(DOWNGRADE_RETENTION_STEP2_DIALOG, DowngradeRetentionStep2DialogComponent),
  provideDialog(CONFIRM_DIALOG, ConfirmDialogComponent),
  provideDialog(MANAGE_SEATS_DIALOG, ManageSeatsComponent),

  // --- Service abstractions -----------------------------------------------------------------------
  { provide: CHECKOUT_BILLING_SERVICE, useExisting: BillingV3Service },
  {
    provide: BILLING_ERROR_NOTIFIER,
    useFactory:
      (errorService: ErrorService): BillingErrorNotifier =>
      (error, message) =>
        errorService.notify(error, message),
    deps: [ErrorService],
  },

  // --- Store dispatch callbacks -------------------------------------------------------------------
  {
    provide: UPDATE_NEXT_PHASE,
    useFactory: (store: Store) => (args: { subscriptionConfigurationId: string; promoCodes: string[] }) =>
      store.dispatch(BillingStateActions.updateNextPhase(args)),
    deps: [Store],
  },
  {
    provide: OPEN_PRICING_PLANS_MODAL,
    useFactory: (store: Store) => () => store.dispatch(BillingStateActions.openPricingPlansModal()),
    deps: [Store],
  },
  {
    provide: OPEN_CHECKOUT_MODAL,
    useFactory: (store: Store) => (selectedPlanId: string) =>
      store.dispatch(BillingStateActions.openCheckoutModal({ selectedPlanId })),
    deps: [Store],
  },
  {
    provide: GET_SETUP_URL,
    useFactory: (store: Store) => (redirectUrl: string) =>
      store.dispatch(BillingStateActions.getSetupUrl({ redirectUrl })),
    deps: [Store],
  },
  {
    provide: SELECT_PLAN,
    useFactory: (store: Store) => (plan: PricingPlan) => store.dispatch(BillingStateActions.selectPlan({ plan })),
    deps: [Store],
  },
  {
    provide: GET_COMPANY_BILLING_COUNTS,
    useFactory: (store: Store) => () => store.dispatch(BillingStateActions.getCompanyBillingCounts()),
    deps: [Store],
  },
  {
    provide: CHANGE_LICENSE_COUNT,
    useFactory: (store: Store) => (quantity: number) =>
      store.dispatch(BillingStateActions.changeLicenseCount({ quantity })),
    deps: [Store],
  },
  {
    provide: AUTO_INCREMENT_SUBSCRIPTION_QUANTITY,
    useFactory: (store: Store) => (quantity: number) =>
      store.dispatch(SubscriptionActions.autoIncrementSubscriptionQuantityBillingPage({ quantity })),
    deps: [Store],
  },
  {
    provide: CANCEL_LICENSE_CHANGE,
    useFactory: (store: Store) => (userId: string) =>
      store.dispatch(BillingStateActions.cancelLicenseChange({ userId })),
    deps: [Store],
  },
  {
    provide: SHOW_INTERCOM_MESSENGER,
    useFactory: (store: Store) => () => store.dispatch(NavMenuIntercomActions.showMessenger()),
    deps: [Store],
  },
  {
    provide: OPEN_RETENTION_STEP2_DIALOG,
    useFactory: (store: Store) => (data: Parameters<typeof BillingStateActions.openRetentionStep2Dialog>[0]['data']) =>
      store.dispatch(BillingStateActions.openRetentionStep2Dialog({ data })),
    deps: [Store],
  },
  {
    provide: DOWNGRADE_CONFIRMED,
    useFactory: (store: Store) => (plan: PricingPlan) =>
      store.dispatch(BillingStateActions.downgradeConfirmed({ plan })),
    deps: [Store],
  },
  {
    provide: RECORD_CANCELLATION_REASON,
    useFactory: (store: Store) => (args: Parameters<typeof BillingStateActions.recordCancellationReason>[0]) =>
      store.dispatch(BillingStateActions.recordCancellationReason(args)),
    deps: [Store],
  },
  {
    provide: GET_COMMERCIAL_MODEL_SUBSCRIPTIONS,
    useFactory: (store: Store) => () => store.dispatch(BillingStateActions.getCommercialModelSubscriptions()),
    deps: [Store],
  },

  // --- Store selector streams ---------------------------------------------------------------------
  {
    provide: MANAGE_SEATS_BILLING_COUNTS,
    useFactory: (store: Store): Observable<BillingCounts> => store.select(selectCompanyBillingCounts),
    deps: [Store],
  },
  {
    provide: IS_TRIAL_SUBSCRIPTION,
    useFactory: (store: Store): Observable<boolean> => store.select(selectIsTrialSubscription),
    deps: [Store],
  },
  {
    provide: MANAGE_SEATS_IS_FREEMIUM_PLAN,
    useFactory: (store: Store): Observable<boolean> => store.select(selectIsFreemiumPlan),
    deps: [Store],
  },
  {
    provide: IS_ADMIN_OR_OWNER,
    useFactory: (store: Store): Observable<boolean> => store.select(selectCurrentUserIsAdminOrOwner),
    deps: [Store],
  },
  {
    provide: USE_SUBSCRIPTION_SCHEDULE_FLOW,
    useFactory: (store: Store): Observable<boolean> => store.select(selectUseSubscriptionScheduleFlow),
    deps: [Store],
  },
  {
    provide: HAS_DEFAULT_PAYMENT_METHOD,
    useFactory: (store: Store): Observable<boolean> => store.select(selectHasDefaultPaymentMethod),
    deps: [Store],
  },
  {
    provide: FREEMIUM_PLAN_ID,
    useFactory: (store: Store): Observable<string> => store.select(selectFreemiumPlanId),
    deps: [Store],
  },
  {
    provide: SORTED_PRICING_PLANS,
    useFactory: (store: Store): Observable<PricingPlan[]> => store.select(selectSortedPricingPlans),
    deps: [Store],
  },
];
